import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  Download,
  Eye,
  FileText,
  Scale,
  Search,
  Upload,
  X,
} from 'lucide-react'
import { TopBar } from '../components/TopBar'
import { useI18n } from '../lib/i18n'
import type { KnowledgeFile } from '../lib/types'

// ─── i18n ────────────────────────────────────────────────────────────────────
const LABELS = {
  zh: {
    pageTitle: '社区法律基金会',
    backToList: '返回基金会',
    files: '个文件',
    updatedAt: '更新于',
    searchFiles: '搜索文档...',
    noFiles: '该主题下暂无文件',
    aiAssistant: 'AI 法律助手',
    aiDesc: '基于当前主题的法律知识进行问答',
    inputPlaceholder: '输入关于此主题的法律问题...',
    typePrecedent: '判例文件',
    typeStatute: '法律法规',
    parsed: '已解析',
    chunks: '个片段',
    articles: '篇文章',
    viewDoc: '查看文档',
    closeDoc: '关闭',
    loading: '加载中...',
    noContent: '暂无文档内容',
    page: '页',
    court: '法院',
    date: '日期',
    citation: '引用',
    viewOriginal: '查看原文件',
    chatHistory: '历史记录',
    newChat: '新对话',
    deleteChat: '删除',
    noHistory: '暂无历史记录',
    uploadDoc: '上传文档',
    uploadTitle: '上传公共法律文档',
    uploadDesc: '支持 PDF、DOCX 格式，将自动进行 OCR 识别和文本提取',
    docTitle: '文档标题',
    docType: '文档类型',
    selectFile: '选择文件',
    uploading: '上传中...',
    uploadSuccess: '上传成功',
    uploadError: '上传失败',
    cancel: '取消',
    confirm: '确认上传',
    typePrecedentDoc: '判例',
    typeStatuteDoc: '法规',
    typeRegulation: '条例',
    typeOther: '其他',
  },
  en: {
    pageTitle: 'Community Law Foundation',
    backToList: 'Back to Foundation',
    files: 'files',
    updatedAt: 'Updated',
    searchFiles: 'Search documents...',
    noFiles: 'No files in this topic',
    aiAssistant: 'AI Legal Assistant',
    aiDesc: 'Q&A based on this topic\'s legal knowledge',
    inputPlaceholder: 'Ask a legal question about this topic...',
    typePrecedent: 'Case Files',
    typeStatute: 'Legal Statutes',
    parsed: 'Parsed',
    chunks: 'chunks',
    articles: 'articles',
    viewDoc: 'View Document',
    closeDoc: 'Close',
    loading: 'Loading...',
    noContent: 'No content available',
    page: 'Page',
    court: 'Court',
    date: 'Date',
    citation: 'Citation',
    viewOriginal: 'View Original File',
    chatHistory: 'History',
    newChat: 'New Chat',
    deleteChat: 'Delete',
    noHistory: 'No history yet',
    uploadDoc: 'Upload',
    uploadTitle: 'Upload Legal Document',
    uploadDesc: 'Supports PDF, DOCX. OCR and text extraction will be performed automatically',
    docTitle: 'Document Title',
    docType: 'Document Type',
    selectFile: 'Select File',
    uploading: 'Uploading...',
    uploadSuccess: 'Upload successful',
    uploadError: 'Upload failed',
    cancel: 'Cancel',
    confirm: 'Upload',
    typePrecedentDoc: 'Precedent',
    typeStatuteDoc: 'Statute',
    typeRegulation: 'Regulation',
    typeOther: 'Other',
  },
}

interface LegalTopic {
  topic_key: string
  name_zh: string
  name_en: string
  desc_zh: string
  desc_en: string
  keywords: string[]
  file_count: number
  last_updated?: string
}

export function LegalBaseDetail() {
  const { topicKey } = useParams<{ topicKey: string }>()
  const navigate = useNavigate()
  const { lang } = useI18n()
  const t = LABELS[lang] ?? LABELS.zh

  const [topic, setTopic] = useState<LegalTopic | null>(null)
  
  // Upload state
  const [showUpload, setShowUpload] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!topicKey) return
    fetch(`/knowledge/legal-topics/${topicKey}`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.json()
      })
      .then((data: LegalTopic) => setTopic(data))
      .catch(() => setTopic(null))
  }, [topicKey])

  // Files
  const [files, setFiles] = useState<KnowledgeFile[]>([])
  const [filesTotal, setFilesTotal] = useState(0)
  const [loadingFiles, setLoadingFiles] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [previewFile, setPreviewFile] = useState<KnowledgeFile | null>(null)

  // Load files from configurable legal topic
  const loadFiles = useCallback(() => {
    if (!topicKey) {
      setLoadingFiles(false)
      return
    }
    setLoadingFiles(true)
    const query = searchTerm ? `&q=${encodeURIComponent(searchTerm)}` : ''
    fetch(`/knowledge/legal-topics/${topicKey}/files?limit=50${query}`)
      .then((r) => r.json())
      .then((data: { files?: KnowledgeFile[]; total?: number }) => {
        setFiles(data.files ?? [])
        setFilesTotal(data.total ?? 0)
      })
      .catch(() => {})
      .finally(() => setLoadingFiles(false))
  }, [topicKey, searchTerm])

  useEffect(() => {
    loadFiles()
  }, [loadFiles])

  // Upload handler
  const handleFileUpload = async (file: File, docTitle: string, docType: string) => {
    if (!topicKey) return
    setUploading(true)
    setUploadProgress(lang === 'zh' ? '正在上传...' : 'Uploading...')

    const formData = new FormData()
    formData.append('file', file)

    try {
      const response = await fetch(
        `/knowledge/legal-documents/upload?topic_key=${encodeURIComponent(topicKey)}&doc_title=${encodeURIComponent(docTitle)}&doc_type=${encodeURIComponent(docType)}`,
        {
          method: 'POST',
          body: formData,
        }
      )

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      await response.json()
      setUploadProgress(t.uploadSuccess)
      setTimeout(() => {
        setShowUpload(false)
        setUploadProgress('')
        loadFiles()
      }, 1500)
    } catch (error) {
      console.error('Upload error:', error)
      setUploadProgress(t.uploadError)
      setTimeout(() => setUploadProgress(''), 3000)
    } finally {
      setUploading(false)
    }
  }

  const name = topic
    ? (lang === 'zh' ? topic.name_zh : topic.name_en)
    : topicKey ?? ''
  const desc = topic
    ? (lang === 'zh' ? topic.desc_zh : topic.desc_en)
    : ''

  return (
    <>
      <TopBar title={t.pageTitle} />
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {/* 顶部标题栏 */}
        <div className="flex items-center gap-4 px-6 py-4 border-b border-brand-200 bg-white flex-shrink-0">
          <button
            type="button"
            onClick={() => navigate('/legal-base')}
            className="p-1.5 rounded-lg text-brand-400 hover:bg-brand-100 hover:text-brand-600 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <Scale className="w-5 h-5 text-blue-600" />
              <h2 className="text-lg font-bold text-brand-800 truncate">{name}</h2>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-700">
                {filesTotal} {t.files}
              </span>
            </div>
            {desc && <p className="text-xs text-brand-400 mt-0.5">{desc}</p>}
          </div>
        </div>

        {/* 主内容：文件列表 */}
        <div className="flex flex-1 min-h-0">
          {/* 文件列表 */}
          <div className="flex-1 border-r border-brand-200 bg-white flex flex-col">
            {/* 搜索和上传 */}
            <div className="px-4 py-3 border-b border-brand-100">
              <div className="flex items-center gap-2 mb-2">
                <div className="relative flex-1">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={t.searchFiles}
                    className="w-full pl-8 pr-3 py-2 rounded-lg border border-brand-200 text-sm text-brand-700 placeholder:text-brand-400 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setShowUpload(true)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-600 text-white text-xs font-medium hover:bg-blue-700 transition-colors whitespace-nowrap"
                >
                  <Upload className="w-3.5 h-3.5" />
                  {t.uploadDoc}
                </button>
              </div>
            </div>

            {/* 文件列表 */}
            <div className="flex-1 overflow-y-auto p-3">
              {loadingFiles ? (
                <div className="flex items-center justify-center py-12 text-sm text-brand-400">
                  Loading...
                </div>
              ) : files.length === 0 ? (
                <div className="flex items-center justify-center py-12 text-sm text-brand-400">
                  {t.noFiles}
                </div>
              ) : (
                <div className="space-y-2">
                  {files.map((file) => (
                    <FileCard
                      key={file.precedent_id}
                      file={file}
                      t={t}
                      lang={lang}
                      onClick={() => setPreviewFile(file)}
                      onPreview={() => setPreviewFile(file)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 文件预览弹窗 */}
      {previewFile && (
        <FilePreviewModal
          file={previewFile}
          t={t}
          lang={lang}
          onClose={() => setPreviewFile(null)}
        />
      )}

      {/* 上传对话框 */}
      {topicKey && (
        <UploadDialog
          show={showUpload}
          uploading={uploading}
          uploadProgress={uploadProgress}
          fileInputRef={fileInputRef}
          onClose={() => {
            setShowUpload(false)
            setUploadProgress('')
          }}
          onUpload={handleFileUpload}
          t={t}
        />
      )}
    </>
  )
}

// ─── 文件卡片 ────────────────────────────────────────────────────────────────
function FileCard({
  file,
  t,
  lang,
  onClick,
  onPreview,
}: {
  file: KnowledgeFile
  t: (typeof LABELS)['zh']
  lang: string
  onClick: () => void
  onPreview: () => void
}) {
  const statuteKeywords = [
    '法令',
    '法规',
    'legislation',
    'statute',
    'amendment_act',
    'regulations',
    'subsidiary_legislation',
    'commencement_order',
    'schedule_amendment',
    'fees',
  ]
  const titleText = `${file.case_name ?? ''} ${file.citation ?? ''}`.toLowerCase()
  const hasStatuteTag =
    file.categories.some((c) => {
      const category = c.toLowerCase()
      return statuteKeywords.some((keyword) => category.includes(keyword.toLowerCase()))
    }) ||
    /\b(act|regulations?|order|rules|enactment|ordinance)\b/i.test(titleText)
  const typeTag = hasStatuteTag ? t.typeStatute : t.typePrecedent
  const typeColor = hasStatuteTag
    ? 'bg-blue-100 text-blue-700'
    : 'bg-amber-100 text-amber-700'
  const iconColor = hasStatuteTag ? 'text-blue-500' : 'text-red-400'

  return (
    <div className="bg-white rounded-xl border border-brand-200 p-3.5 hover:border-blue-200 hover:shadow-sm transition-all">
      <div className="flex items-start gap-3">
        <FileText className={`w-4 h-4 mt-0.5 flex-shrink-0 ${iconColor}`} />
        <div className="flex-1 min-w-0 cursor-pointer" onClick={onClick}>
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-sm font-medium text-brand-800 truncate">
              {file.case_name ?? file.precedent_id}
            </span>
            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0 ${typeColor}`}>
              {typeTag}
            </span>
          </div>
          <div className="text-xs text-brand-400">
            {file.decision_date ? `PDF · ${file.decision_date}` : `PDF · ${file.precedent_id.slice(0, 8)}`}
            {file.n_chunks > 0 && (
              <span className="ml-2 text-emerald-600">✓ {t.parsed} · {file.n_chunks} {t.chunks}</span>
            )}
          </div>
          {file.citation && (
            <p className="text-xs text-brand-500 mt-1 line-clamp-1">{file.citation}</p>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onPreview()
            }}
            className="p-2 rounded-lg text-blue-600 bg-blue-50 hover:bg-blue-100 transition-colors"
            title={t.viewOriginal || '查看原文件'}
          >
            <Eye className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              const fileId = file.precedent_id
              const fallbackName = file.case_name || file.citation || fileId
              fetch(`/knowledge/legal-documents/${fileId}/file`)
                .then((r) => {
                  const cd = r.headers.get('Content-Disposition') || ''
                  const match = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i)
                  const downloadName = match ? decodeURIComponent(match[1]) : fallbackName
                  return r.blob().then((blob) => ({ blob, downloadName }))
                })
                .then(({ blob, downloadName }) => {
                  const url = URL.createObjectURL(blob)
                  const a = document.createElement('a')
                  a.href = url
                  a.download = downloadName
                  a.click()
                  URL.revokeObjectURL(url)
                })
            }}
            className="p-2 rounded-lg text-green-600 bg-green-50 hover:bg-green-100 transition-colors"
            title={lang === 'zh' ? '下载' : 'Download'}
          >
            <Download className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── 文档查看器 ──────────────────────────────────────────────────────────────
// ─── 上传对话框 ──────────────────────────────────────────────────────────────
function UploadDialog({
  show,
  uploading,
  uploadProgress,
  fileInputRef,
  onClose,
  onUpload,
  t,
}: {
  show: boolean
  uploading: boolean
  uploadProgress: string
  fileInputRef: React.RefObject<HTMLInputElement>
  onClose: () => void
  onUpload: (file: File, title: string, docType: string) => void
  t: (typeof LABELS)['zh']
}) {
  const [docTitle, setDocTitle] = useState('')
  const [docType, setDocType] = useState('statute')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)

  const handleSubmit = () => {
    if (!selectedFile || !docTitle.trim() || uploading) return
    onUpload(selectedFile, docTitle.trim(), docType)
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setSelectedFile(file)
      if (!docTitle) {
        const name = file.name.replace(/\.[^/.]+$/, '')
        setDocTitle(name)
      }
    }
  }

  if (!show) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
        <div className="flex items-start justify-between mb-5">
          <div>
            <h3 className="text-lg font-semibold text-brand-800">{t.uploadTitle}</h3>
            <p className="text-sm text-brand-400 mt-1">{t.uploadDesc}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={uploading}
            className="p-1.5 rounded-lg text-brand-400 hover:bg-brand-100 hover:text-brand-600 transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-brand-700 mb-2">
              {t.docTitle}
            </label>
            <input
              type="text"
              value={docTitle}
              onChange={(e) => setDocTitle(e.target.value)}
              disabled={uploading}
              placeholder={t.docTitle}
              className="w-full px-3 py-2 rounded-lg border border-brand-200 text-sm focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 disabled:bg-brand-50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-brand-700 mb-2">
              {t.docType}
            </label>
            <select
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
              disabled={uploading}
              className="w-full px-3 py-2 rounded-lg border border-brand-200 text-sm focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 disabled:bg-brand-50"
            >
              <option value="statute">{t.typeStatuteDoc}</option>
              <option value="precedent">{t.typePrecedentDoc}</option>
              <option value="regulation">{t.typeRegulation}</option>
              <option value="other">{t.typeOther}</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-brand-700 mb-2">
              {t.selectFile}
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx"
              onChange={handleFileChange}
              disabled={uploading}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-full px-4 py-3 rounded-lg border-2 border-dashed border-brand-300 text-sm text-brand-600 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {selectedFile ? (
                <div className="flex items-center justify-center gap-2">
                  <FileText className="w-4 h-4" />
                  <span className="truncate">{selectedFile.name}</span>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  <Upload className="w-4 h-4" />
                  <span>{t.selectFile}</span>
                </div>
              )}
            </button>
          </div>

          {uploadProgress && (
            <div className={`text-sm text-center py-2 rounded-lg ${
              uploadProgress.includes('成功') || uploadProgress.includes('successful')
                ? 'bg-green-50 text-green-700'
                : uploadProgress.includes('失败') || uploadProgress.includes('failed')
                ? 'bg-red-50 text-red-700'
                : 'bg-blue-50 text-blue-700'
            }`}>
              {uploadProgress}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button
            type="button"
            onClick={onClose}
            disabled={uploading}
            className="px-4 py-2 rounded-lg border border-brand-200 text-sm text-brand-600 hover:bg-brand-50 transition-colors disabled:opacity-50"
          >
            {t.cancel}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!selectedFile || !docTitle.trim() || uploading}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploading ? t.uploading : t.confirm}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── 文件预览弹窗 ────────────────────────────────────────────────────────────
function FilePreviewModal({
  file,
  t,
  lang,
  onClose,
}: {
  file: KnowledgeFile
  t: (typeof LABELS)['zh']
  lang: string
  onClose: () => void
}) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [text, setText] = useState<string | null>(null)
  const [viewMode, setViewMode] = useState<'file' | 'text'>('file')
  const [fileFormat, setFileFormat] = useState<string>('')

  const fileId = file.precedent_id
  const fileName = file.case_name || file.citation || fileId

  useEffect(() => {
    let objectUrl: string | null = null
    setLoading(true)

    // 先获取文件信息以确定格式
    fetch(`/knowledge/legal-documents/${fileId}`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.json()
      })
      .then((data: { file_format?: string; extracted_text?: string; source_file?: string }) => {
        const format = data.file_format || ''
        setFileFormat(format)
        setText(data.extracted_text || null)

        const isPdf = format.toLowerCase() === 'pdf'
        const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(format.toLowerCase())

        // 如果是PDF或图片，获取原始文件用于预览
        if (isPdf || isImage) {
          return fetch(`/knowledge/legal-documents/${fileId}/view`)
            .then((r) => {
              if (!r.ok) throw new Error(`HTTP ${r.status}`)
              return r.blob()
            })
            .then((blob) => {
              objectUrl = URL.createObjectURL(blob)
              setBlobUrl(objectUrl)
            })
        }
      })
      .catch(() => {
        setBlobUrl(null)
        setText(null)
      })
      .finally(() => setLoading(false))

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [fileId])

  const isPdf = fileFormat.toLowerCase() === 'pdf'
  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(fileFormat.toLowerCase())

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] mx-4 flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-brand-200 flex-shrink-0">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-bold text-brand-800 truncate">{fileName}</h3>
            <div className="flex items-center gap-3 mt-1 text-xs text-brand-400">
              {file.n_chunks > 0 && (
                <span className="text-green-600">
                  {t.parsed} · {file.n_chunks} {t.chunks}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* 视图切换按钮 */}
            {(isPdf || isImage) && text && (
              <div className="flex items-center gap-1 px-2 py-1 bg-brand-100 rounded-lg">
                <button
                  type="button"
                  onClick={() => setViewMode('file')}
                  className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                    viewMode === 'file'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-brand-600 hover:text-brand-800'
                  }`}
                >
                  {lang === 'zh' ? '原文件' : 'Original'}
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('text')}
                  className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                    viewMode === 'text'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-brand-600 hover:text-brand-800'
                  }`}
                >
                  {lang === 'zh' ? '提取文本' : 'Extracted'}
                </button>
              </div>
            )}
            <button
              type="button"
              onClick={() => {
                fetch(`/knowledge/legal-documents/${fileId}/file`)
                  .then((r) => {
                    const cd = r.headers.get('Content-Disposition') || ''
                    const match = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i)
                    const downloadName = match ? decodeURIComponent(match[1]) : fileName
                    return r.blob().then((blob) => ({ blob, downloadName }))
                  })
                  .then(({ blob, downloadName }) => {
                    const url = URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    a.href = url
                    a.download = downloadName
                    a.click()
                    URL.revokeObjectURL(url)
                  })
              }}
              className="p-2 rounded-lg text-brand-400 hover:bg-brand-100 hover:text-brand-600 transition-colors"
              title={lang === 'zh' ? '下载' : 'Download'}
            >
              <Download className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-lg text-brand-400 hover:bg-brand-100 hover:text-brand-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20 text-sm text-brand-400">
              {t.loading}
            </div>
          ) : viewMode === 'file' && isPdf && blobUrl ? (
            <iframe
              src={blobUrl}
              className="w-full h-full min-h-[70vh]"
              title={fileName}
            />
          ) : viewMode === 'file' && isImage && blobUrl ? (
            <div className="flex items-center justify-center p-6 bg-brand-50">
              <img
                src={blobUrl}
                alt={fileName}
                className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-sm"
              />
            </div>
          ) : viewMode === 'text' && text ? (
            <div className="p-6">
              <div className="text-sm text-brand-700 leading-relaxed whitespace-pre-wrap font-mono bg-brand-50 rounded-xl p-5">
                {text}
              </div>
            </div>
          ) : !isPdf && !isImage && text ? (
            <div className="p-6">
              <div className="text-sm text-brand-700 leading-relaxed whitespace-pre-wrap font-mono bg-brand-50 rounded-xl p-5">
                {text}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <FileText className="w-12 h-12 text-brand-200 mb-3" />
              <div className="text-sm text-brand-500 mb-1">
                {t.noContent}
              </div>
              <div className="text-xs text-brand-400 mb-4">
                {lang === 'zh' ? '请下载后查看' : 'Please download to view'}
              </div>
              <button
                type="button"
                onClick={() => {
                  fetch(`/knowledge/legal-documents/${fileId}/file`)
                    .then((r) => {
                      const cd = r.headers.get('Content-Disposition') || ''
                      const match = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i)
                      const downloadName = match ? decodeURIComponent(match[1]) : fileName
                      return r.blob().then((blob) => ({ blob, downloadName }))
                    })
                    .then(({ blob, downloadName }) => {
                      const url = URL.createObjectURL(blob)
                      const a = document.createElement('a')
                      a.href = url
                      a.download = downloadName
                      a.click()
                      URL.revokeObjectURL(url)
                    })
                }}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
              >
                <Download className="w-4 h-4" />
                {lang === 'zh' ? '下载文件' : 'Download'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
